import React, { useState, useContext } from 'react'
import styled, { css } from 'styled-components'
import { number } from 'prop-types'
import ScrollAnimation from 'react-animate-on-scroll'

import { media } from 'styles/media'
import { square, animationTime, animationCurve } from 'styles/global'
import { white } from 'styles/colors'
import * as spacing from 'styles/spacing'

import NativeAuthContext from 'components/NativeAuth'
import FeaturedProduct from 'components/home/FeaturedProduct'
import FlavourShowcase from 'components/home/FlavourShowcase'
import FlavourCarousel from 'components/home/FlavourShowcase/FlavourCarousel'

const Container = styled.div`
  position: relative;
  overflow: hidden;
`

const Inner = styled.div`
  display: flex;
  height: 700px;
  width: ${props => `${100 * props.steps}vw`};
  transition: transform 0.5s cubic-bezier(0.455, 0.03, 0.515, 0.955);
  transform: translateX(${props => `${(-100 / props.steps) * props.step}%`});

  ${media.small`
    height: 782px;
  `};

  ${media.medium`
    height: 760px;
    transition-duration: 1s;
  `};

  ${media.large`
    height: 860px;
  `};

  ${media.xLarge`
    height: 900px;
  `};

  ${media.xxLarge`
    height: 1100px;
  `};
`

const Slide = styled.div`
  position: relative;
  height: 100%;
  width: 100vw;
`

const Wipe = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 40;
  background-color: #ca9801;
  transition: transform 1s cubic-bezier(0.455, 0.03, 0.515, 0.955);
  transition-delay: 0.4s;

  ${props =>
    props.step === 1 &&
    css`
      transform: translateX(-100%);
    `}
`

const Dots = styled.div`
  display: flex;
  justify-content: center;
  padding-top: ${spacing.large};
  padding-bottom: ${spacing.xLarge};

  ${media.small`
    padding-top: ${spacing.large};
    padding-bottom: ${spacing.xLarge};
  `};

  ${media.medium`
    padding-top: ${spacing.large};
    padding-bottom: ${spacing.xxLarge};
  `};

  ${media.large`
    padding-top: ${spacing.xLarge};
    padding-bottom: ${spacing.xxxLarge};
  `};
`

const Dot = styled.div`
  background-color: ${white};
  border-radius: 50%;
  opacity: 0.3;
  cursor: pointer;
  transition: opacity ${animationTime} ${animationCurve};
  ${square('10px')};

  &:hover {
    opacity: 0.5;
  }

  & + & {
    margin-left: ${spacing.small};
  }

  ${props =>
    props.active &&
    css`
      opacity: 1;

      &:hover {
        opacity: 1;
      }
    `}
`

const EpenSlideshow = ({ steps }) => {
  const [step, setStep] = useState(0)
  const { province } = useContext(NativeAuthContext)

  if (province === 'BC') return null

  return (
    <Container>
      <Inner step={step} steps={steps}>
        <Slide>
          <FeaturedProduct
            link="/buy-online/epen-3/"
            heading="ePen"
            subheading="6 COLOURS | 8 FLAVOURS"
            price="$9.99"
            cta="EXPLORE FLAVOURS"
            handleCtaClick={() => setStep(1)}
            device="epen"
            gradient="linear-gradient(254.88deg, #A48F75 -13.48%, #ECDCC9 160.24%)"
            accentColor="#b69d83"
            navigation={[
              {
                label: 'FLAVOURS',
                icon: 'pods',
                onClick: () => setStep(1)
              }
            ]}
          />
        </Slide>
        <Slide>
          <Wipe step={step} className="u-hide-medium-down" />
          <FlavourCarousel
            navigation={[
              {
                label: 'ePen',
                icon: 'epen',
                onClick: () => setStep(0)
              }
            ]}
            flavours={[
              {
                name: 'Mango',
                prettyName: 'Mango',
                description: 'A rich mango flavour immersed with fruity notes',
                link: '/buy-online/vype-epen-3-cartridges/tropical-mango-vpro-epen-3-cartridge/',
                price: '$13.00',
                slug: 'mango',
                background: 'linear-gradient(110.85deg, #DBA700 -7.61%, #9C6E00 115.55%), #DEAA04'
              },
              {
                name: 'Strawberry',
                prettyName: 'Strawberry',
                description: 'A fusion of strawberry with green fruit notes',
                link: '/buy-online/vype-epen-3-cartridges/original-strawberry-vpro-epen-3-cartridge/',
                price: '$13.00',
                slug: 'strawberry',
                background: 'linear-gradient(110.85deg, #DF525C -7.61%, #A4101C 115.55%), #DD0C34'
              },
              {
                name: 'Peach',
                prettyName: 'Peach',
                description: 'White peach with a hint of delicate floral notes',
                link: '/buy-online/vype-epen-3-cartridges/classic-peach-vpro-epen-3-cartridge/',
                price: '$13.00',
                slug: 'peach',
                background: 'linear-gradient(110.85deg, #E9684D -7.61%, #BB321B 113.2%), #EE7A5F'
              },
              {
                name: 'Apple',
                prettyName: 'Apple',
                description: 'A refreshing mix of green apple and cool mint flavours',
                link: '/buy-online/vype-epen-3-cartridges/fresh-apple-vpro-epen-3-cartridge/',
                price: '$13.00',
                slug: 'apple',
                background: 'linear-gradient(110.85deg, #7AA85A -7.61%, #295924 113.2%), #C0D994'
              },
              {
                name: 'Vanilla',
                prettyName: 'Vanilla',
                description: 'A rich vanilla taste with a smooth texture',
                link: '/buy-online/vype-epen-3-cartridges/infused-vanilla-vpro-epen-3-cartridge/',
                price: '$13.00',
                slug: 'vanilla',
                background: 'linear-gradient(110.85deg, #CA9957 -7.61%, #725628 113.2%), #9B7438'
              },
              {
                name: 'Crisp Mint',
                prettyName: 'Crisp<br/> Mint',
                description: 'A deep cooling spearmint and peppermint flavour',
                link: '/buy-online/vype-epen-3-cartridges/vape-pods-crushed-mint-epen-3-cartridge/',
                price: '$13.00',
                slug: 'mint',
                background: 'linear-gradient(110.85deg, #5D9892 -7.61%, #0B3C37 115.55%), #073833'
              },
              {
                name: 'Cherry',
                prettyName: 'Cherry',
                description: 'A deep yet light blend of dark cherry and red fruit notes',
                link: '/buy-online/vype-epen-3-cartridges/dark-cherry-vpro-epen-3-cartridge/',
                price: '$13.00',
                slug: 'cherry',
                background: 'linear-gradient(110.85deg, #AD2E72 -7.61%, #5C143A 115.55%), #A55199'
              },
              {
                name: 'Classic Tobacco',
                prettyName: 'Classic<br/> Tobacco',
                description: 'A high intensity flavour with a darker smokier taste',
                link: '/buy-online/vype-epen-3-cartridges/classic-tobacco-vpro-epen-3-cartridge/',
                price: '$13.00',
                slug: 'classic-tobacco',
                background: 'linear-gradient(110.85deg, #D5783E -7.61%, #934924 115.55%), #934924'
              },
              {
                name: '0 mg/ml',
                prettyName: 'Nicotine<br/> Free',
                description: '0 mg/ml',
                link: '/buy-online/vype-epen-3-cartridges/',
                price: '$13.00',
                slug: 'nicotine-free',
                background: 'linear-gradient(110.85deg, #82C1EE -7.61%, #2E77BA 98.87%), #314BA7'
              }
            ]}
          />
          <FlavourShowcase
            handleCtaClick={() => setStep(0)}
            ctaLabel="DISCOVER ePen"
            flavours={[
              {
                name: 'Mango',
                prettyName: 'Mango',
                description: 'A rich mango flavour immersed with fruity notes',
                link: '/buy-online/vype-epen-3-cartridges/tropical-mango-vpro-epen-3-cartridge',
                price: '$13.00',
                slug: 'mango',
                visible: true,
                background: 'linear-gradient(110.85deg, #DBA700 -7.61%, #9C6E00 115.55%), #DEAA04'
              },
              {
                name: 'Strawberry',
                prettyName: 'Strawberry',
                description: 'A fusion of strawberry with green fruit notes',
                link: '/buy-online/vype-epen-3-cartridges/original-strawberry-vpro-epen-3-cartridge',
                price: '$13.00',
                slug: 'strawberry',
                visible: true,
                background: 'linear-gradient(110.85deg, #DF525C -7.61%, #A4101C 115.55%), #DD0C34'
              },
              {
                name: '0 mg/ml',
                prettyName: 'Nicotine<br/> Free',
                description: '0 mg/ml',
                link: '/buy-online/vype-epen-3-cartridges',
                price: '$13.00',
                slug: 'nicotine-free',
                visible: true,
                background: 'linear-gradient(110.85deg, #82C1EE -7.61%, #2E77BA 98.87%), #314BA7'
              },
              {
                name: 'Peach',
                prettyName: 'Peach',
                description: 'White peach with a hint of delicate floral notes',
                link: '/buy-online/vype-epen-3-cartridges/classic-peach-vpro-epen-3-cartridge',
                price: '$13.00',
                slug: 'peach',
                visible: true,
                background: 'linear-gradient(110.85deg, #E9684D -7.61%, #BB321B 113.2%), #EE7A5F'
              },
              {
                name: 'Apple',
                prettyName: 'Apple',
                description: 'A refreshing mix of green apple and cool mint flavours',
                link: '/buy-online/vype-epen-3-cartridges/fresh-apple-vpro-epen-3-cartridge',
                price: '$13.00',
                slug: 'apple',
                visible: true,
                background: 'linear-gradient(110.85deg, #7AA85A -7.61%, #295924 113.2%), #C0D994'
              },
              {
                name: 'Vanilla',
                prettyName: 'Vanilla',
                description: 'A rich vanilla taste with a smooth texture',
                link: '/buy-online/vype-epen-3-cartridges/infused-vanilla-vpro-epen-3-cartridge',
                price: '$13.00',
                slug: 'vanilla',
                visible: true,
                background: 'linear-gradient(110.85deg, #CA9957 -7.61%, #725628 113.2%), #9B7438'
              },
              {
                name: 'Crisp Mint',
                prettyName: 'Crisp<br/> Mint',
                description: 'A deep cooling spearmint and peppermint flavour',
                link: '/buy-online/vype-epen-3-cartridges/vape-pods-crushed-mint-epen-3-cartridge',
                price: '$13.00',
                slug: 'mint',
                visible: true,
                background: 'linear-gradient(110.85deg, #5D9892 -7.61%, #0B3C37 115.55%), #073833'
              },
              {
                name: 'Cherry',
                prettyName: 'Cherry',
                description: 'A deep yet light blend of dark cherry and red fruit notes',
                link: '/buy-online/vype-epen-3-cartridges/dark-cherry-vpro-epen-3-cartridge',
                price: '$13.00',
                slug: 'cherry',
                visible: true,
                background: 'linear-gradient(110.85deg, #AD2E72 -7.61%, #5C143A 115.55%), #A55199'
              },
              {
                name: 'Classic Tobacco',
                prettyName: 'Classic<br/> Tobacco',
                description: 'A high intensity flavour with a darker smokier taste',
                link: '/buy-online/vype-epen-3-cartridges/classic-tobacco-vpro-epen-3-cartridge',
                price: '$13.00',
                slug: 'classic-tobacco',
                visible: true,
                background: 'linear-gradient(110.85deg, #D5783E -7.61%, #934924 115.55%), #934924'
              }
            ]}
          />
        </Slide>
      </Inner>
      <Dots as={ScrollAnimation} animateIn="fadeInUp" offset={0} delay={1200} animateOnce>
        <Dot onClick={() => setStep(0)} active={step === 0} />
        <Dot onClick={() => setStep(1)} active={step === 1} />
      </Dots>
    </Container>
  )
}

EpenSlideshow.propTypes = {
  steps: number.isRequired
}

export default EpenSlideshow
